import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { THEME, gradients, shadows } from '../constants/theme';

const TermsAndConditions = () => {
  const [activeSection, setActiveSection] = useState('definitions');

  const sections = [
    { id: 'definitions', title: 'Definitions', icon: '📚' },
    { id: 'accessing', title: 'Accessing the Platform', icon: '🔑' },
    { id: 'user-content', title: 'User Content', icon: '📝' },
    { id: 'subscriptions', title: 'Subscriptions', icon: '💳' },
    { id: 'intellectual-property', title: 'Intellectual Property', icon: '©️' },
    { id: 'warranties', title: 'Disclaimer of Warranties', icon: '⚠️' },
    { id: 'liability', title: 'Limitation of Liability', icon: '⚖️' },
    { id: 'indemnification', title: 'Indemnification', icon: '🛡️' },
    { id: 'governing-law', title: 'Governing Law', icon: '📜' },
    { id: 'changes', title: 'Changes to Terms', icon: '🔄' },
    { id: 'agreement', title: 'Entire Agreement', icon: '📋' },
    { id: 'contact', title: 'Contact Us', icon: '📧' },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: THEME.background }}>
      {/* Fixed Sidebar */}
      <div className="fixed top-0 left-0 h-full w-64 overflow-y-auto"
        style={{ backgroundColor: THEME.cardbg, boxShadow: shadows.lg }}>
        <div className="p-6" style={{ background: gradients.primary }}>
          <Link to="/" className="flex items-center space-x-2">
            <img src="/gradient_logo.png" alt="Aided" className="h-8 w-8" />
            <span className="text-xl font-bold text-white">Aided</span>
          </Link>
        </div>
        <nav className="p-4">
          <div className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`
                  w-full text-left px-4 py-2 rounded-lg text-sm font-medium
                  transition-colors duration-150 ease-in-out
                  flex items-center space-x-2
                `}
                style={{
                  backgroundColor: activeSection === section.id ? THEME.primaryLight + '20' : 'transparent',
                  color: activeSection === section.id ? THEME.primary : THEME.text
                }}
              >
                <span className="text-lg">{section.icon}</span>
                <ChevronRightIcon 
                  className={`h-4 w-4 transition-transform duration-200 ${
                    activeSection === section.id ? 'transform rotate-90' : ''
                  }`}
                />
                <span>{section.title}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="pl-64">
        <div className="max-w-4xl mx-auto px-8 py-12">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl font-bold mb-4" style={{ color: THEME.text }}>Terms and Conditions</h1>
            <div className="flex items-center space-x-4 text-sm" style={{ color: THEME.subtext }}>
              <span>Effective Date: 2025-01-16</span>
              <span>•</span>
              <span>Last Updated: 2025-01-16</span>
            </div>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg mb-12" style={{ color: THEME.text }}>
            <p>
              Welcome to Aided! These Terms and Conditions ("Terms") govern your access to and use of our website, 
              mobile application, and services (collectively, the "Platform"). Please read these Terms carefully before 
              using the Platform. By accessing or using the Platform, you agree to be bound by these Terms. If you 
              disagree with any part of the Terms, then you may not access the Platform.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-16">
            <section id="definitions" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">📚</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Definitions</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <ul className="list-disc pl-6 space-y-2">
                  <li>"Aided" refers to the company providing the Platform, including its affiliates, directors, officers, employees, agents, and licensors.</li>
                  <li>"Content" refers to any information, data, text, software, music, sound, photographs, graphics, video, messages, or other materials generated or uploaded by users or Aided on the Platform.</li>
                  <li>"User" refers to any individual or entity that accesses or uses the Platform.</li>
                  <li>"Subscription" refers to the purchase of a premium plan on the Platform, granting access to additional features and benefits.</li>
                </ul>
              </div>
            </section>

            <section id="accessing" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">🔑</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Accessing the Platform</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Eligibility:</strong> You must be at least 18 years old or the legal age of majority in your jurisdiction to access and use the Platform.</li>
                  <li><strong>Account Responsibility:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</li>
                  <li><strong>Platform Availability:</strong> Aided reserves the right to modify, suspend, or discontinue the Platform or any part of it at any time without prior notice.</li>
                </ul>
              </div>
            </section>

            <section id="user-content" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">📝</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>User Content</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Ownership:</strong> You retain ownership of any Content you upload or generate on the Platform.</li>
                  <li><strong>License:</strong> By uploading Content, you grant Aided a non-exclusive, worldwide, royalty-free license to use, reproduce, modify, adapt, publish, translate, distribute, and display the Content.</li>
                  <li><strong>Acceptable Use:</strong> You agree not to upload any Content that is unlawful, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable.</li>
                  <li><strong>Content Removal:</strong> Aided reserves the right to remove any Content that violates these Terms or that Aided deems objectionable.</li>
                </ul>
              </div>
            </section>

            <section id="subscriptions" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">💳</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Subscriptions</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Premium Plans:</strong> Aided offers various premium subscription plans with additional features and benefits.</li>
                  <li><strong>Subscription Renewals:</strong> Subscriptions will automatically renew at the end of each billing cycle unless cancelled.</li>
                  <li><strong>Subscription Cancellations:</strong> You may cancel your subscription at any time through your account settings.</li>
                </ul>
              </div>
            </section>

            <section id="intellectual-property" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">©️</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Intellectual Property</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Platform Ownership:</strong> The Platform and its Content are protected by copyright, trademark, and other intellectual property laws.</li>
                  <li><strong>Trademarks:</strong> Aided's trademarks and logos may not be used without express written permission.</li>
                </ul>
              </div>
            </section>

            <section id="warranties" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">⚠️</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Disclaimer of Warranties</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Platform Use:</strong> The Platform is provided "as is" and without warranty of any kind.</li>
                  <li><strong>Content Accuracy:</strong> Aided does not warrant that the Content is accurate, complete, or current.</li>
                </ul>
              </div>
            </section>

            <section id="liability" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">⚖️</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Limitation of Liability</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Indirect Damages:</strong> Aided shall not be liable for any indirect, incidental, special, consequential, or punitive damages.</li>
                  <li><strong>Total Liability:</strong> Aided's total liability shall not exceed the amount of subscription fees paid in the twelve months preceding the claim.</li>
                </ul>
              </div>
            </section>

            <section id="indemnification" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">🛡️</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Indemnification</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <p>
                  You agree to indemnify and hold Aided harmless from any claims, losses, expenses, damages, and costs 
                  (including attorneys' fees) arising out of or related to your use of the Platform or violation of these Terms.
                </p>
              </div>
            </section>

            <section id="governing-law" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">📜</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Governing Law</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Governing Law:</strong> These Terms shall be governed by the laws of the State of California.</li>
                  <li><strong>Dispute Resolution:</strong> Any disputes shall be settled by binding arbitration in San Francisco, California.</li>
                </ul>
              </div>
            </section>

            <section id="changes" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">🔄</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Changes to Terms</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <p>
                  Aided reserves the right to modify these Terms at any time. Changes will be effective immediately upon posting. 
                  Your continued use of the Platform following any changes constitutes acceptance of those changes.
                </p>
              </div>
            </section>

            <section id="agreement" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">📋</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Entire Agreement</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <p>
                  These Terms constitute the entire agreement between you and Aided regarding your access to and use of the Platform, 
                  superseding any prior or contemporaneous communications and proposals.
                </p>
              </div>
            </section>

            <section id="contact" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">📧</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Contact Us</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <p>
                  If you have any questions or concerns about these Terms, please contact us at{' '}
                  <a href="mailto:support@Aided.com" 
                    className="font-medium hover:underline"
                    style={{ color: THEME.primary }}>
                    support@Aided.com
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions; 